import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import SmoothScroll from "@/components/SmoothScroll";
import SEO from "@/components/SEO";
import NewsletterCTA from "@/components/NewsletterCTA";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ClipboardList,
  ShieldCheck,
  Handshake,
  Sparkles,
  TrendingUp,
  Globe2,
  Users,
  Heart,
  ArrowUpRight,
} from "lucide-react";

const PARTNER_HERO =
  "https://images.unsplash.com/photo-1516011362164-3095a82b0af9?auto=format&fit=crop&w=2400&q=80";

const STEPS = [
  {
    icon: ClipboardList,
    title: "Submit your application",
    body:
      "Tell us about your company, your routes, and the kinds of travelers you already host. A short form &mdash; takes 5 minutes.",
  },
  {
    icon: ShieldCheck,
    title: "Verification & licence check",
    body:
      "We review your business licence, insurance and references. Most applicants hear back within 7 working days.",
  },
  {
    icon: Handshake,
    title: "Onboarding call",
    body:
      "A 45-minute walk-through of our standards, pricing model, and the operator portal where you list and manage tours.",
  },
  {
    icon: Sparkles,
    title: "Go live &mdash; together",
    body:
      "We co-promote your first three tours on the journal, the homepage, and to our concierge desk. Bookings start flowing.",
  },
];

const BENEFITS = [
  {
    icon: Globe2,
    title: "An international audience",
    body:
      "Reach travelers from Europe, Australia, North America and Japan &mdash; the curated clientele our marketing is built around.",
  },
  {
    icon: TrendingUp,
    title: "Transparent commercials",
    body:
      "A fair, flat commission published up-front. No hidden fees, no surprise deductions, weekly settlements in IDR or USD.",
  },
  {
    icon: Users,
    title: "Dedicated concierge support",
    body:
      "Your guests get our 24/7 concierge, including pre-arrival logistics, in-trip support, and language assistance.",
  },
  {
    icon: Heart,
    title: "A small, respectful network",
    body:
      "We work with a deliberately small circle of operators we know personally. Quality over volume &mdash; always.",
  },
];

const REQUIREMENTS = [
  "Indonesian-registered tour operator, guide collective, hotel, or boat owner",
  "Valid business licence (NIB / SIUP / TDUP equivalent) and operator insurance",
  "Minimum 2 years guiding international or domestic travelers",
  "English-speaking lead contact &mdash; additional languages a plus",
  "Commitment to fair labour and conservation-led practices",
];

export default function Partner() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState({
    company_name: "",
    business_license_url: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get("/partners/me");
        if (r.data && r.data.partner_id) setExisting(r.data);
      } catch (_) {
        // ignore — user simply hasn't applied yet
      }
    })();
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return navigate("/login");
    setSubmitting(true);
    try {
      await api.post("/partners/apply", form);
      toast.success("Application submitted. We'll review shortly.");
      refresh();
      const r = await api.get("/partners/me");
      setExisting(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SmoothScroll>
      <div className="min-h-screen bg-[#FDFBF7]">
        <SEO
          title="Partnership &mdash; Join our network of local Indonesian operators"
          description="Are you a local Indonesian operator, guide, or hotel? Apply to partner with Across Indonesia and reach a curated international audience."
          image={PARTNER_HERO}
        />
        <Navbar transparent />

        <PageHero
          image={PARTNER_HERO}
          eyebrow={t("partner.eyebrow")}
          title={t("partner.title_a")}
          titleBold={t("partner.title_b")}
          subtitle={t("partner.intro")}
        />

        {/* Stats banner */}
        <section className="bg-white border-y border-stone-100">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { n: "240+", label: "Local partners onboard" },
              { n: "38", label: "Provinces covered" },
              { n: "97%", label: "Repeat or referral guests" },
              { n: "7 days", label: "Average review time" },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-4xl md:text-5xl font-light tracking-tight text-slate-900">
                  <b className="font-bold text-seafoam">{s.n}</b>
                </div>
                <div className="mt-2 text-xs uppercase tracking-[0.2em] text-slate-500">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </section>

        <main
          className="max-w-7xl mx-auto px-6 md:px-12"
          data-testid="partner-page"
        >
          {/* Step-by-step process */}
          <section className="py-24 md:py-32">
            <div className="grid md:grid-cols-12 gap-12 items-start mb-16">
              <div className="md:col-span-5">
                <div className="t-overline text-seafoam mb-5">How it works</div>
                <h2 className="t-h2 text-slate-900">
                  Four <b>quiet</b><br />
                  steps to partnership.
                </h2>
              </div>
              <p className="md:col-span-7 md:pt-12 text-slate-600 text-lg leading-relaxed">
                Our onboarding is deliberately measured &mdash; we want to make
                sure both sides fit. From your application to your first
                booking is typically two to four weeks.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8" data-testid="partner-steps">
              {STEPS.map((s, i) => (
                <div
                  key={s.title}
                  className="relative bg-white rounded-2xl p-8 border border-stone-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-500"
                >
                  <div className="absolute -top-4 -right-4 w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-semibold tracking-wider shadow-md">
                    0{i + 1}
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-seafoam/10 border border-seafoam/20 flex items-center justify-center mb-5">
                    <s.icon size={20} strokeWidth={1.6} className="text-seafoam" />
                  </div>
                  <h3 className="text-xl font-light tracking-tight text-slate-900 leading-snug">
                    <b className="font-bold">{s.title}</b>
                  </h3>
                  <p
                    className="mt-3 text-sm text-slate-600 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: s.body }}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* Benefits */}
          <section className="py-24 md:py-28 border-t border-stone-100">
            <div className="grid md:grid-cols-12 gap-12 items-start mb-12">
              <div className="md:col-span-5">
                <div className="t-overline text-seafoam mb-5">Why partner with us</div>
                <h2 className="t-h2 text-slate-900">
                  What you <b>get back.</b>
                </h2>
              </div>
              <p className="md:col-span-7 md:pt-12 text-slate-600 text-lg leading-relaxed">
                We invest in our partners as carefully as our travelers. Here is
                what that looks like in practice.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
              {BENEFITS.map((b) => (
                <div key={b.title} className="flex gap-6">
                  <div className="w-14 h-14 rounded-full bg-stone-100 border border-stone-200 flex items-center justify-center shrink-0">
                    <b.icon size={20} strokeWidth={1.5} className="text-slate-700" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-light tracking-tight text-slate-900">
                      <b className="font-bold">{b.title}</b>
                    </h3>
                    <p
                      className="mt-3 text-slate-600 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: b.body }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Requirements */}
          <section className="py-24 md:py-28 border-t border-stone-100">
            <div className="grid md:grid-cols-12 gap-12">
              <div className="md:col-span-5">
                <div className="t-overline text-seafoam mb-5">Eligibility</div>
                <h2 className="t-h2 text-slate-900">
                  Who we <b>partner with.</b>
                </h2>
                <p className="mt-6 text-slate-600 leading-relaxed">
                  We&apos;re a small team and we believe in working closely with
                  every operator we add. We currently consider applicants who
                  meet all the following criteria.
                </p>
              </div>
              <ul className="md:col-span-7 space-y-4">
                {REQUIREMENTS.map((r, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-stone-100"
                  >
                    <div className="w-8 h-8 rounded-full bg-seafoam/10 text-seafoam font-semibold text-sm flex items-center justify-center shrink-0">
                      {i + 1}
                    </div>
                    <span
                      className="text-slate-700 leading-relaxed pt-0.5"
                      dangerouslySetInnerHTML={{ __html: r }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Application area */}
          <section className="py-20 md:py-24 border-t border-stone-100">
            <div className="t-overline text-seafoam mb-5">Apply</div>
            <h2 className="t-h2 text-slate-900 mb-10">
              Ready to <b>apply?</b>
            </h2>

            {!user && (
              <div className="bg-white rounded-2xl p-8 border border-stone-100 max-w-2xl">
                <p className="text-slate-600 leading-relaxed">
                  Sign in first so we can keep track of your application and
                  reach back out to you.
                </p>
                <Link
                  to="/login"
                  data-testid="partner-login-prompt"
                  className="mt-6 inline-flex btn-pill bg-slate-900 text-white hover:bg-slate-800 text-sm"
                >
                  {t("partner.sign_in_to_apply")}
                  <ArrowUpRight size={15} strokeWidth={1.6} />
                </Link>
              </div>
            )}

            {user && existing && (
              <div
                className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100 max-w-2xl"
                data-testid="partner-status"
              >
                <div className="t-overline text-slate-500">{t("partner.status")}</div>
                <div className="text-2xl font-light mt-2 capitalize">
                  <b>{existing.status}</b>
                </div>
                <div className="text-sm text-slate-600 mt-1">{existing.company_name}</div>
                {existing.status === "approved" && (
                  <Link
                    to="/partner/portal"
                    data-testid="goto-partner-portal"
                    className="mt-6 inline-flex btn-pill bg-seafoam text-white hover:bg-[#7A988F] text-sm"
                  >
                    {t("partner.open_portal")}
                    <ArrowUpRight size={15} strokeWidth={1.6} />
                  </Link>
                )}
              </div>
            )}

            {user && !existing && (
              <form
                onSubmit={submit}
                className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-stone-100 grid gap-5 max-w-3xl"
              >
                <label className="block">
                  <span className="t-overline text-slate-500">{t("partner.company")}</span>
                  <input
                    data-testid="partner-company"
                    required
                    value={form.company_name}
                    onChange={(e) =>
                      setForm({ ...form, company_name: e.target.value })
                    }
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-seafoam"
                  />
                </label>
                <label className="block">
                  <span className="t-overline text-slate-500">{t("partner.license")}</span>
                  <input
                    data-testid="partner-license"
                    value={form.business_license_url}
                    onChange={(e) =>
                      setForm({ ...form, business_license_url: e.target.value })
                    }
                    placeholder="Link to your business licence (Google Drive, Dropbox, etc.)"
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-seafoam"
                  />
                </label>
                <label className="block">
                  <span className="t-overline text-slate-500">
                    {t("partner.description")}
                  </span>
                  <textarea
                    data-testid="partner-description"
                    required
                    rows={5}
                    value={form.description}
                    onChange={(e) =>
                      setForm({ ...form, description: e.target.value })
                    }
                    placeholder="Tell us about your company, the routes you operate, and the kind of traveler you host best."
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-seafoam resize-none"
                  />
                </label>
                <button
                  data-testid="partner-submit"
                  disabled={submitting}
                  type="submit"
                  className="btn-pill bg-slate-900 text-white hover:bg-slate-800 justify-center disabled:opacity-50"
                >
                  {submitting ? t("partner.submitting") : t("partner.submit")}
                  <ArrowUpRight size={15} strokeWidth={1.6} />
                </button>
              </form>
            )}
          </section>
        </main>

        {/* Journal newsletter — fills the gap before the footer */}
        <div className="pt-4 md:pt-6">
          <NewsletterCTA source="partner" />
        </div>

        <Footer />
      </div>
    </SmoothScroll>
  );
}
